import React from 'react';
import { FmlxButton, FmlxDateRangePicker } from 'fmlx-common-ui';
import { LabelRadioBox } from 'components/customComponents/Layers/InputGroups';
import NumberSelectControl from 'components/customComponents/NumberSelectControl';
import OverlayBanner from 'components/generic/overlayBanner';
import { useModalHandlers } from 'components/modal';
import { useRMDispatch } from 'redux/hooks';
import { useSelectPreviewFilters } from 'redux/hooks/archiving';
import {
  setFilterType,
  setFilterField,
  setOlderThanValue,
  setOlderThanUnit,
  setStartDate,
  setEndDate,
  resetPreviewFilters,
} from 'redux/reducers/archivingReducer';
import {
  usePreviewArchiving,
  usePreviewFilterState,
  useIsArchivingJobProcessing,
} from './hooks';
import {
  EPreviewFilterField,
  EPreviewFilterType,
  EOlderThanUnit,
} from 'business/models/Models';
import {
  ARCHIVING_FILTER_RESET_MESSAGE,
  OlderThanFilterOptions,
  FILTER_CHANGED_WARNING,
} from './constants';
import DateHelper from 'utils/DateHelper';

type FiltersProps = {
  selectedNodes: number[];
  onPreviewTabSelect?: () => void;
  disabled?: boolean;
};
export const Filters = (props: FiltersProps) => {
  const { selectedNodes, onPreviewTabSelect, disabled = false } = props;
  const previewFilters = useSelectPreviewFilters();
  const {
    filterType,
    filterField,
    olderThanValue,
    olderThanUnit,
    startDate,
    endDate,
    isPreviewFiltered,
    isFilterChangedAfterPreview,
  } = previewFilters || {
    filterType: EPreviewFilterType.OlderThan,
    filterField: EPreviewFilterField.None,
    olderThanValue: 1,
    olderThanUnit: EOlderThanUnit.Years,
    startDate: '',
    endDate: '',
    isPreviewFiltered: false,
    isFilterChangedAfterPreview: false,
  };
  const { showModal, hideModal } = useModalHandlers();
  const dispatch = useRMDispatch();

  const { filtersEnabled, isPreviewDisabled, isResetDisabled } =
    usePreviewFilterState(selectedNodes);
  const isJobProcessing = useIsArchivingJobProcessing();
  const areFilterFieldsDisabled = disabled || selectedNodes.length === 0;
  const areFilterDetailsDisabled = disabled || !filtersEnabled;
  const isPreviewButtonDisabled = disabled || isPreviewDisabled || isJobProcessing;
  const isResetButtonDisabled = disabled || isResetDisabled || isJobProcessing;

  const warningModalProps = {
    heading: 'Update Preview',
    body: 'Experiment List will be previewed without filter. Are you sure you want to continue?',
    okButtonText: 'Confirm',
    noButtonText: 'Close',
    onOk: () => {
      dispatch(resetPreviewFilters());
      onPreviewTabSelect?.();
      onPreviewClick(selectedNodes, true, false, ARCHIVING_FILTER_RESET_MESSAGE);
      hideModal();
    },
    inlineMessage: { type: 'warning' as 'warning' | 'error', message: 'Reset Filter' },
  };

  const { onPreviewClick } = usePreviewArchiving();

  const handlePreviewClick = () => {
    onPreviewTabSelect?.();
    onPreviewClick(selectedNodes);
  };

  const applyDefaultFilterType = () => {
    dispatch(setFilterType(EPreviewFilterType.OlderThan));
    dispatch(setOlderThanValue(1));
    dispatch(setOlderThanUnit(EOlderThanUnit.Years));
  };

  const onFilterByDateCreationChange = () => {
    dispatch(setFilterField(EPreviewFilterField.CreationDate));
    applyDefaultFilterType();
  };
  const onFilterByInspectionDateChange = () => {
    dispatch(setFilterField(EPreviewFilterField.InspectionDate));
    applyDefaultFilterType();
  };

  const onFilterTypeChange = (type: EPreviewFilterType) => {
    dispatch(setFilterType(type));
  };

  const onResetClick = () => {
    if (isPreviewFiltered) {
      showModal(warningModalProps);
    } else {
      dispatch(resetPreviewFilters());
    }
  };

  return (
    <>
      <OverlayBanner
        id="archiving-filter-changed-banner"
        show={!!isFilterChangedAfterPreview}
        variant="warning"
        title={FILTER_CHANGED_WARNING}
        hideCloseButton
      />
      <div className="flex flex-col text-sm w-full basis-[36%] max-h-[36%] gap-1">
        <div className="text-sm font-semibold">Select one filter to preview</div>
        <div className="flex flex-col basis-[90%] max-h-[90%] overflow-auto gap-3">
          <div className="flex flex-row">
            <div className="flex-1">
              <LabelRadioBox
                disabled={areFilterFieldsDisabled}
                labelText="Filter By Date Creation"
                radioValue={filterField === EPreviewFilterField.CreationDate}
                onChange={onFilterByDateCreationChange}
                name="filter-by-date"
              />
            </div>
            <div className="flex-1">
              <LabelRadioBox
                disabled={areFilterFieldsDisabled}
                labelText="Filter By Inspection Date"
                radioValue={filterField === EPreviewFilterField.InspectionDate}
                onChange={onFilterByInspectionDateChange}
                name="filter-by-date"
              />
            </div>
          </div>
          <div className="flex flex-col gap-3 px-4 w-full">
            <div>
              <LabelRadioBox
                disabled={areFilterDetailsDisabled}
                labelText="Older than"
                radioValue={filterType === EPreviewFilterType.OlderThan}
                onChange={() => onFilterTypeChange(EPreviewFilterType.OlderThan)}
                name="Older than"
              />
            </div>
            <div className="flex items-center gap-1">
              <div>Older than</div>
              <NumberSelectControl
                className="h-6"
                disabled={
                  areFilterDetailsDisabled || filterType !== EPreviewFilterType.OlderThan
                }
                options={OlderThanFilterOptions}
                value={olderThanValue}
                setValue={(v: number) => dispatch(setOlderThanValue(v))}
                unit={olderThanUnit}
                setUnit={(val: string) => dispatch(setOlderThanUnit(parseInt(val)))}
              />
            </div>
            <div>
              <LabelRadioBox
                disabled={areFilterDetailsDisabled}
                labelText="Date Range"
                radioValue={filterType === EPreviewFilterType.DateRange}
                onChange={() => onFilterTypeChange(EPreviewFilterType.DateRange)}
                name="Date Range"
              />
            </div>
            <div className="flex flex-row  gap-4 w-full">
              <FmlxDateRangePicker
                startValue={startDate}
                endValue={endDate}
                onPickStartDate={(value) => {
                  dispatch(setStartDate(value));
                }}
                onPickEndDate={(value) => {
                  dispatch(setEndDate(value));
                }}
                labelStartDate="Start Date"
                labelEndDate="End Date"
                labelPosition={FmlxDateRangePicker.LabelPosition.TOP}
                disabled={
                  areFilterDetailsDisabled || filterType !== EPreviewFilterType.DateRange
                }
                maxDate={DateHelper.formatDate(new Date(), 'YYYY-MM-DD')}
              />
            </div>
          </div>
          <div className="flex flex-row-reverse gap-2">
            <FmlxButton
              disabled={isPreviewButtonDisabled}
              onClick={handlePreviewClick}
              label="Preview"
            />
            <FmlxButton
              disabled={isResetButtonDisabled}
              onClick={onResetClick}
              label="Reset"
            />
          </div>
        </div>
      </div>
    </>
  );
};
